// The web's mirror of the pricing rules.
//
// The bundle cannot import Go, and the draft has to total instantly as a
// cashier taps: a round trip per tap would make the till feel broken. So these
// functions exist twice, which is a risk taken deliberately and paid for
// by the golden vectors: internal/core/domain writes every case it computes to
// testdata/vectors.json, and vectors.test.ts reads that same file.
//
// The server remains the truth. Quote and checkout recompute everything, and a
// disagreement is settled by the side that charges the money -- so when a
// vector fails, this file is wrong, never the vector.

export const MIN_QTY = 1;
export const MAX_QTY = 999;
export const MAX_MINOR = 1_000_000_000;
/** A weighed portion's bounds, in whole grams (UC-3.R10). */
export const MIN_GRAMS = 1;
export const MAX_GRAMS = 100_000;

export interface LineModifier {
	id: string;
	name: string;
	/**
	 * What the choice adds to the unit; a per-kg choice's amount for its
	 * portion, so an older bundle prices the line alike.
	 */
	priceDeltaMinor: number;
	/** A per-kg group's choice names its kind and rate (UC-3.R11). */
	kind?: 'fixed' | 'perKg';
	perKgMinor?: number;
}

export interface Line {
	itemId: string;
	name: string;
	/** The item's effective price for this shop, already resolved. */
	unitBaseMinor: number;
	modifiers?: LineModifier[];
	qty: number;
	note?: string;
	/**
	 * A weighed portion's grams and kilo price (UC-3.R9). Such a line's base is
	 * its weight, never unitBaseMinor; the screen keeps unitBaseMinor equal to
	 * that base so an older bundle prices it alike.
	 */
	weight?: LineWeight;
}

/** A weighed line's sale weight and the price per kilogram it was sold at. */
export interface LineWeight {
	grams: number;
	perKgMinor: number;
}

export interface Totals {
	unitMinor: number[];
	lineTotalMinor: number[];
	subtotalMinor: number;
	deliveryMinor: number;
	/** What the order's discount takes off, 0 with none (UC-35). */
	discountMinor: number;
	totalMinor: number;
}

/**
 * The discount an order is given (UC-35): one from the owner's list, with its
 * id and name, or one a manager typed, with neither.
 */
export interface OrderDiscount {
	discountId?: string;
	name?: string;
	kind: DiscountKind;
	value: number;
}

/** The code a refusal carries, matching the server's. */
export type PricingErrorCode = 'NEGATIVE_LINE' | 'VALIDATION_ERROR';

export class PricingError extends Error {
	constructor(
		readonly code: PricingErrorCode,
		readonly path: string,
	) {
		super(code);
		this.name = 'PricingError';
	}
}

/**
 * Prices an order.
 *
 *   unit       = base + sum(modifier deltas)      // never below zero
 *   line_total = qty * unit                       // 1 <= qty <= 999
 *   subtotal   = sum(line_total)
 *   discount   = discountMinor(subtotal + delivery, kind, value)  // 0 with none
 *   total      = subtotal + delivery - discount
 *
 * A line with weight takes its base as its grams at the kilo price, rounded
 * half up, as the Go does, never unitBaseMinor; it has qty 1 and
 * MIN_GRAMS..MAX_GRAMS grams, else VALIDATION_ERROR. A per-kg choice adds
 * perKgChoiceMinor(perKgMinor, grams), each rounded on its own, never its
 * priceDeltaMinor; on a line with no weight it is VALIDATION_ERROR at
 * lines[i].modifiers. After the NEGATIVE_LINE check, a per-kg amount or a
 * weighed unit outside ±MAX_MINOR is VALIDATION_ERROR at lines[i].grams.
 *
 * A unit below zero throws rather than clamping, exactly as the server refuses
 * rather than clamping: clamping would sell a dish for nothing because somebody
 * priced a removal wrongly, and the screen would look as though it had worked.
 *
 * The discount comes off the subtotal and the delivery fee together, rounded
 * to a whole unit of the currency (`minorDigits` places), and never more than
 * that base (UC-35).
 */
export function recomputeTotals(
	lines: Line[],
	deliveryMinor: number,
	discount: OrderDiscount | null,
	minorDigits: number,
): Totals {
	if (deliveryMinor < 0 || deliveryMinor > MAX_MINOR) {
		throw new PricingError('VALIDATION_ERROR', 'deliveryFeeMinor');
	}

	const unitMinor: number[] = [];
	const lineTotalMinor: number[] = [];
	let subtotalMinor = 0;

	lines.forEach((line, i) => {
		if (line.qty < MIN_QTY || line.qty > MAX_QTY) {
			throw new PricingError('VALIDATION_ERROR', `lines[${i}]`);
		}
		let unit = line.unitBaseMinor;
		const weight = line.weight;
		if (weight) {
			if (line.qty !== 1) {
				throw new PricingError('VALIDATION_ERROR', `lines[${i}].qty`);
			}
			if (weight.grams < MIN_GRAMS || weight.grams > MAX_GRAMS) {
				throw new PricingError('VALIDATION_ERROR', `lines[${i}].grams`);
			}
			// perKgMinor <= MAX_MINOR and grams <= MAX_GRAMS keep the product
			// below 2^53, so it is exact here as in Go.
			unit = Math.floor((weight.perKgMinor * weight.grams + 500) / 1000);
		}
		let outOfBounds = false;
		for (const modifier of line.modifiers ?? []) {
			if (modifier.kind !== 'perKg') {
				unit += modifier.priceDeltaMinor;
				continue;
			}
			if (!weight) {
				throw new PricingError('VALIDATION_ERROR', `lines[${i}].modifiers`);
			}
			const amount = perKgChoiceMinor(modifier.perKgMinor ?? 0, weight.grams);
			if (amount > MAX_MINOR || amount < -MAX_MINOR) outOfBounds = true;
			unit += amount;
		}
		if (unit < 0) {
			throw new PricingError('NEGATIVE_LINE', `lines[${i}]`);
		}
		if (weight && (outOfBounds || unit > MAX_MINOR)) {
			throw new PricingError('VALIDATION_ERROR', `lines[${i}].grams`);
		}
		const lineTotal = line.qty * unit;
		unitMinor.push(unit);
		lineTotalMinor.push(lineTotal);
		subtotalMinor += lineTotal;
	});

	const base = subtotalMinor + deliveryMinor;
	const off = discount ? discountMinor(base, discount.kind, discount.value, minorDigits) : 0;
	return {
		unitMinor,
		lineTotalMinor,
		subtotalMinor,
		deliveryMinor,
		discountMinor: off,
		totalMinor: base - off,
	};
}

/**
 * A per-kg choice's amount on a portion (UC-3.R11): its rate a kilogram for
 * grams, its magnitude rounded half up and its sign kept, as the Go's
 * PerKgChoiceMinor. Exact below 2^53, as |rate| <= MAX_MINOR and grams <=
 * MAX_GRAMS keep it.
 */
export function perKgChoiceMinor(perKgMinor: number, grams: number): number {
	return Math.sign(perKgMinor) * Math.floor((Math.abs(perKgMinor) * grams + 500) / 1000);
}

/** What a shop charges: the override when it set one, the base otherwise. */
export function effectivePrice(
	baseMinor: number,
	overrideMinor: number | null | undefined,
): number {
	// Not `??` on a falsy check: zero is a real price, and a giveaway is not
	// the same thing as no override.
	return overrideMinor === null || overrideMinor === undefined ? baseMinor : overrideMinor;
}

/** Whether a shop is selling it. Read the same way, and separately. */
export function effectiveAvailability(
	baseAvailable: boolean,
	overrideAvailable: boolean | null | undefined,
): boolean {
	return overrideAvailable === null || overrideAvailable === undefined
		? baseAvailable
		: overrideAvailable;
}

/** A discount's kind (UC-34): a whole percent, or an amount in minor units. */
export type DiscountKind = 'percent' | 'amount';

/**
 * The most a percent discount may be: the whole order, so a staff meal is free
 * (domain.MaxDiscountPercent). The owner's list and a manager's typed discount
 * keep to it.
 */
export const MAX_DISCOUNT_PERCENT = 100;

/**
 * What a discount takes off its base, the subtotal plus the delivery fee
 * (UC-35). A percent rounds down to a whole unit of the currency, so 15% of
 * 87.00 takes 13.00; 100% takes the whole base. An amount never takes more
 * than the base. In BigInt, because a large base times a percent passes
 * 2^53, where a number would round.
 */
export function discountMinor(
	baseMinor: number,
	kind: DiscountKind,
	value: number,
	minorDigits: number,
): number {
	if (baseMinor <= 0 || value <= 0) return 0;
	if (kind === 'amount') return Math.min(value, baseMinor);
	if (value >= MAX_DISCOUNT_PERCENT) return baseMinor;
	const unit = 10n ** BigInt(minorDigits);
	return Number(((BigInt(baseMinor) * BigInt(value)) / 100n / unit) * unit);
}
